import React from "react";
import { Modal } from "./index";
import { Ticket, User, Calendar, Clock, DollarSign } from "lucide-react";

const SalesConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  isCreatingReservation,
  selectedEvent,
  selectedSeats,
  allSeats,
  customerInfo,
  totalPrice,
  isFreeTicket,
  freeTicketReason,
  freeTicketAccessCode,
  isModifyingReservations,
  existingReservations,
  reservationsToCancel,
  reservationsToReturn,
  isBlocked,
}) => {
  const formatPrice = (price) => {
    const numericPrice = typeof price === 'number' ? price : parseFloat(price) || 0;
    return numericPrice.toFixed(2);
  };

  const getSeatInfo = (seatId) => {
    return allSeats?.find(seat => seat.compositeId === seatId) || allSeats?.find(seat => seat.id === seatId);
  };

  const getSectionName = (seatId) => {
    if (seatId.startsWith('BOX-')) return 'Palcos';
    return seatId.startsWith('PB-') ? 'Planta Baja' : '1º Piso';
  };

  const isPalco = (seatId) => {
    const seat = getSeatInfo(seatId);
    return seat && (seat.section?.startsWith('palco') || seat.type?.toLowerCase().includes('palco'));
  };

  const formatSeatName = (seatId) => {
    // Si es un compositeId, extraer la información
    if (seatId.includes('-')) {
      const parts = seatId.split('-');
      if (parts[0] === 'BOX') {
        return `Palco ${parts[1]}`;
      } else if (parts[0] === 'PB') {
        return `Fila ${parts[1]} - Asiento ${parts[2]}`;
      } else if (parts[0] === 'PP') {
        return `Fila ${parts[1]} - Asiento ${parts[2]}`;
      }
    }
    
    // Si no es un compositeId, buscar en allSeats
    const seat = allSeats.find(s => s.compositeId === seatId) || allSeats.find(s => s.id === seatId);
    if (seat) {
      if (seat.section?.startsWith('palco') || seat.type?.toLowerCase().includes('palco')) {
        return `Palco ${seat.number}`;
      } else {
        return `Fila ${seat.row} - Asiento ${seat.number}`;
      }
    }
    
    // Fallback: mostrar el ID original
    return seatId;
  };

  const groupedSeats = selectedSeats.reduce((acc, seatId) => {
    const sectionName = getSectionName(seatId);
    if (!acc[sectionName]) {
      acc[sectionName] = [];
    }
    acc[sectionName].push(seatId);
    return acc;
  }, {});

  // Calcular precio total considerando devoluciones y entradas ya vendidas
  const calculateEffectiveTotalPrice = () => {
    let total = 0;
    
    // Sumar precio de asientos seleccionados (nuevos)
    selectedSeats.forEach(seatId => {
      const seat = getSeatInfo(seatId);
      if (seat) {
        total += seat.price || 0;
      }
    });
    
    // Si estamos modificando reservas, considerar devoluciones con precios negativos
    if (isModifyingReservations && existingReservations.length > 0) {
      existingReservations.forEach(reservation => {
        const isMarkedForReturn = reservationsToReturn.some(r => r.id === reservation.id);
        
        // Si la reserva está marcada para devolución y era 'sold', restar su precio (efecto negativo)
        if (isMarkedForReturn && reservation.status === 'sold' && reservation.price) {
          total -= reservation.price;
        }
      });
    }
    
    return total;
  };

  // Usar el totalPrice pasado como prop o calcular internamente
  const effectiveTotalPrice = typeof totalPrice === 'number' ? totalPrice : calculateEffectiveTotalPrice();

  const getOperationType = () => {
    if (isBlocked) return "Bloqueo de Entradas";
    if (isFreeTicket) return "Entrada Gratuita";
    if (isModifyingReservations) return "Modificación de Reserva";
    return "Venta de Entradas";
  };

  const getOperationDescription = () => {
    if (isBlocked) return "Se bloquearán los asientos seleccionados sin costo";
    if (isFreeTicket) return "Se creará una entrada gratuita con código de acceso";
    if (isModifyingReservations) return "Se modificarán las reservas existentes";
    return "Se procesará la venta de entradas";
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-blue-600 text-white px-6 py-4 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Ticket className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-semibold">Confirmar {getOperationType()}</h2>
                <p className="text-blue-100 text-sm">{getOperationDescription()}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-blue-100 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Event Information */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <Calendar className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-800">Información del Evento</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Evento</p>
                <p className="font-medium text-gray-800">{selectedEvent?.title}</p>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-600" />
                <div>
                  <p className="text-sm text-gray-600">Fecha y Hora</p>
                  <p className="font-medium text-gray-800">
                    {selectedEvent?.eventDate && new Date(selectedEvent.eventDate).toLocaleDateString()} {selectedEvent?.eventTime}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <User className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-800">Información del Cliente</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Nombre</p>
                <p className="font-medium text-gray-800">{customerInfo?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Teléfono</p>
                <p className="font-medium text-gray-800">{customerInfo?.phone}</p>
              </div>
            </div>
          </div>

          {/* Free Ticket Information */}
          {isFreeTicket && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-800 mb-3">Información de Entrada Gratuita</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-blue-600">Código de Acceso</p>
                  <p className="font-medium text-blue-800">{freeTicketAccessCode}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-600">Motivo</p>
                  <p className="font-medium text-blue-800">{freeTicketReason}</p>
                </div>
              </div>
            </div>
          )}

          {/* Seat Details */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Ticket className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-800">Detalle de Asientos</h3>
            </div>
            
            {Object.keys(groupedSeats).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(groupedSeats).map(([sectionName, seatIds]) => (
                  <div key={sectionName} className="border rounded-lg p-4">
                    <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">{sectionName}</h4>
                    <div className="space-y-2">
                      {seatIds.map((seatId) => {
                        const seatInfo = getSeatInfo(seatId);
                        const isPalcoSeat = isPalco(seatId);
                        
                        // Verificar si es una reserva existente con status 'sold'
                        const existingSoldReservation = existingReservations.find(r => 
                          r.compositeId === seatId && r.status === 'sold'
                        );
                        
                        return (
                          <div key={seatId} className={`flex justify-between items-center p-3 rounded-lg ${
                            existingSoldReservation 
                              ? 'bg-green-50 border border-green-200' 
                              : 'bg-gray-50 border border-gray-200'
                          }`}>
                            <div className="flex items-center gap-3">
                              <div className={`w-3 h-3 rounded-full ${
                                existingSoldReservation ? 'bg-green-500' : 'bg-blue-500'
                              }`}></div>
                              <div>
                                <span className="font-medium text-gray-800">
                                  {sectionName === 'Palcos' || isPalcoSeat
                                    ? `Palco ${seatInfo?.number}`
                                    : `Fila ${seatInfo?.row} - Asiento ${seatInfo?.number}`}
                                </span>
                                {existingSoldReservation && (
                                  <span className="text-green-600 font-medium ml-2">
                                    ✓ Ya vendido
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <span className={`font-semibold ${
                                existingSoldReservation ? 'text-green-600' : 'text-gray-800'
                              }`}>
                                {(existingSoldReservation && parseFloat(existingSoldReservation.price) === 0) || 
                                 (seatInfo && parseFloat(seatInfo.price) === 0)
                                  ? '$0' 
                                  : `$${formatPrice(seatInfo?.price)}`
                                }
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                No hay asientos seleccionados
              </div>
            )}
          </div>

          {/* Modification Summary */}
          {isModifyingReservations && (reservationsToCancel.length > 0 || reservationsToReturn.length > 0) && (
            <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h3 className="text-lg font-semibold text-yellow-800 mb-3">Resumen de Modificaciones</h3>
              <div className="space-y-3">
                {reservationsToCancel.length > 0 && (
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <span className="text-yellow-700 font-medium">
                      {reservationsToCancel.length} asiento(s) a cancelar
                    </span>
                  </div>
                )}
                {reservationsToReturn.length > 0 && (
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                    <span className="text-orange-700 font-medium">
                      {reservationsToReturn.length} asiento(s) a devolver
                    </span>
                  </div>
                )}
              </div>
              
              {/* Mostrar precios de devoluciones */}
              {reservationsToReturn.length > 0 && (
                <div className="mt-4 pt-4 border-t border-yellow-200">
                  <h4 className="font-semibold text-yellow-800 text-sm mb-3">Devoluciones:</h4>
                  <div className="space-y-2">
                    {reservationsToReturn.map(reservation => {
                      const seat = allSeats.find(s => s.compositeId === reservation.compositeId);
                      return (
                        <div key={reservation.id} className="flex justify-between items-center p-2 bg-orange-50 rounded border border-orange-200">
                          <span className="text-orange-700 font-medium">
                            {formatSeatName(reservation.compositeId)}
                          </span>
                          <span className="text-red-600 font-semibold">
                            {reservation.price === 0 
                              ? '$0' 
                              : `-$${(reservation.price || seat?.price || 0).toLocaleString()}`
                            }
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Total */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-3">
                <DollarSign className="w-6 h-6 text-gray-600" />
                <span className="text-lg font-semibold text-gray-800">Total a Pagar</span>
              </div>
              <span className={`text-2xl font-bold ${
                effectiveTotalPrice < 0 ? 'text-red-600' : 'text-green-600'
              }`}>
                ${effectiveTotalPrice < 0 ? '0' : effectiveTotalPrice.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isCreatingReservation}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={isCreatingReservation}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {isCreatingReservation ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Procesando...
              </>
            ) : (
              <>
                <Ticket className="w-4 h-4" />
                Confirmar {getOperationType()}
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SalesConfirmationModal;
